import QRCode from 'qrcode';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ApiError, fmt, myTicketApi,
  type MyTicketDetailDto, type MyTicketListItemDto, type MyTicketScope,
} from '../api';

/**
 * TICK-093 — `/myticket`.
 *
 * The screen order follows what the visitor has already proved, not a fixed funnel:
 *
 *   - a browser that bought something here opens straight onto its tickets;
 *   - a browser whose phone was proved and remembered opens onto every ticket for that number;
 *   - a browser with neither is asked for a phone number.
 *
 * The first two never show a login form, which is the entire point of the feature. The third is not
 * a fallback for a broken case — it is the only route in for a new phone, a cleared cookie, or the
 * LINE in-app browser, which keeps its own cookie jar and so always arrives cold the first time.
 */

type Screen = 'loading' | 'unavailable' | 'phone' | 'otp' | 'list' | 'detail';

/** The one thing about the number worth showing back: enough to recognise, not enough to read out. */
function maskPhone(local: string): string {
  if (local.length < 4) return local;
  return `${local.slice(0, 3)}-xxx-${local.slice(-4)}`;
}

function formatEventDate(iso: string | null): string {
  if (!iso) return '';
  return new Date(iso).toLocaleDateString('th-TH', { day: 'numeric', month: 'short', year: 'numeric' });
}

function TicketRow({ item, onOpen }: { item: MyTicketListItemDto; onOpen: (code: string) => void }) {
  return (
    <button type="button" className={`myticket-row${item.past ? ' is-past' : ''}`} onClick={() => onOpen(item.code)}>
      <span className="myticket-row-head">
        <span>
          <span className="myticket-row-event">{item.eventName}</span>
          <span className="myticket-row-when">
            {formatEventDate(item.eventEndsAt)}{item.eventEndsAt ? ' · ' : ''}{item.qty} ใบ · เข้างานได้ {item.entries} คน
          </span>
        </span>
        <span className={`myticket-pill${item.checkedInAt ? ' is-done' : ''}`}>
          {item.checkedInAt ? 'เข้างานแล้ว' : item.past ? 'จบงานแล้ว' : 'พร้อมใช้'}
        </span>
      </span>
      <span className="myticket-row-foot">
        <span className="myticket-row-code">{item.code}</span>
        <span className="myticket-row-total">{fmt(item.total)}</span>
      </span>
    </button>
  );
}

export default function MyTicket() {
  const [screen, setScreen] = useState<Screen>('loading');
  const [scope, setScope] = useState<MyTicketScope>('none');
  const [orders, setOrders] = useState<MyTicketListItemDto[]>([]);
  const [detail, setDetail] = useState<MyTicketDetailDto | null>(null);
  const [qrDataUrl, setQrDataUrl] = useState('');

  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [remember, setRemember] = useState(true);
  const [attemptsLeft, setAttemptsLeft] = useState<number | null>(null);
  const [ttlLeft, setTtlLeft] = useState(0);
  const [resendIn, setResendIn] = useState(0);

  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  /** Set when the visitor came from a device-scoped list and is widening it, so "back" means something. */
  const [upgrading, setUpgrading] = useState(false);
  /** This browser booked for several people, so it cannot say on its own whose tickets these are. */
  const [deviceShared, setDeviceShared] = useState(false);
  const otpRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => { document.title = 'ตั๋วของฉัน · JamTick'; }, []);

  // The opening question, asked once: what does this browser already have?
  useEffect(() => {
    myTicketApi.session()
      .then(async (s) => {
        setScope(s.scope);
        setDeviceShared(s.deviceShared);
        if (s.scope === 'none') { setScreen('phone'); return; }
        const list = await myTicketApi.list();
        setOrders(list.orders);
        setScope(list.scope);
        /*
         * A cookie with nothing behind it is the reserved-but-never-paid case: the browser made an
         * order, the transfer never cleared, and there is genuinely nothing to show. Landing on an
         * empty list would read as "your tickets are gone"; the phone form is the honest next step,
         * and the OTP route can still find tickets bought elsewhere.
         */
        setScreen(list.orders.length ? 'list' : 'phone');
      })
      .catch((e) => {
        // 404 is the rollout switch being off for this deploy — the one case where the page must
        // not offer a form it cannot honour. Anything else is transient, and the form is still the
        // right next step.
        setScreen(e instanceof ApiError && e.status === 404 ? 'unavailable' : 'phone');
      });
  }, []);

  useEffect(() => {
    if (screen !== 'otp') return undefined;
    const id = window.setInterval(() => {
      setTtlLeft((v) => Math.max(0, v - 1));
      setResendIn((v) => Math.max(0, v - 1));
    }, 1000);
    return () => window.clearInterval(id);
  }, [screen]);

  useEffect(() => {
    if (!detail?.order.qrPayload) { setQrDataUrl(''); return; }
    QRCode.toDataURL(detail.order.qrPayload, { margin: 0, width: 336, color: { dark: '#171717', light: '#fafafa' } })
      .then(setQrDataUrl)
      .catch(() => setQrDataUrl(''));
  }, [detail?.order.qrPayload]);

  const requestOtp = useCallback(async () => {
    if (!/^0\d{9}$/.test(phone)) {
      setError('กรอกเบอร์มือถือ 10 หลักที่ขึ้นต้นด้วย 0');
      return;
    }
    setBusy(true);
    setError('');
    try {
      const res = await myTicketApi.requestOtp(phone);
      setOtp(['', '', '', '', '', '']);
      setAttemptsLeft(null);
      setTtlLeft(res.ttlMinutes * 60);
      setResendIn(res.resendAfterSeconds);
      setScreen('otp');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'ขอรหัสไม่สำเร็จ กรุณาลองใหม่');
    } finally {
      setBusy(false);
    }
  }, [phone]);

  const submitOtp = useCallback(async () => {
    const code = otp.join('');
    if (code.length < 6) { setError('ใส่รหัสให้ครบ 6 หลัก'); return; }
    setBusy(true);
    setError('');
    const res = await myTicketApi.verifyOtp(phone, code, remember);
    setBusy(false);
    if (!res.ok) {
      setError(res.error);
      setAttemptsLeft(res.attemptsLeft);
      setOtp(['', '', '', '', '', '']);
      otpRefs.current[0]?.focus();
      if (res.attemptsLeft <= 0) setScreen('phone');
      return;
    }
    setScope(res.scope);
    setOrders(res.orders);
    setUpgrading(false);
    setScreen('list');
  }, [otp, phone, remember]);

  const openTicket = useCallback(async (code: string) => {
    setBusy(true);
    setError('');
    try {
      setDetail(await myTicketApi.detail(code));
      setScreen('detail');
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'เปิดตั๋วไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, []);

  const logout = useCallback(async () => {
    await myTicketApi.logout().catch(() => {});
    setScope('none');
    setOrders([]);
    setDetail(null);
    setPhone('');
    setUpgrading(false);
    setScreen('phone');
  }, []);

  const onOtpChange = (index: number, raw: string) => {
    const digit = raw.replace(/\D/g, '').slice(0, 1);
    setOtp((prev) => prev.map((v, i) => (i === index ? digit : v)));
    if (digit && index < 5) otpRefs.current[index + 1]?.focus();
  };

  const upcoming = orders.filter((o) => !o.past);
  const past = orders.filter((o) => o.past);

  return (
    <div className="page myticket-page">
      <header className="shell container myticket-header">
        <a className="logo" href="/">JAMTICK</a>
        <span className="myticket-header-label">ตั๋วของฉัน</span>
      </header>

      <main className="shell container myticket-main">
        {screen === 'loading' && <p className="myticket-muted" aria-live="polite">กำลังตรวจสอบ…</p>}

        {screen === 'unavailable' && (
          <section className="myticket-card">
            <h1>ยังไม่เปิดให้ใช้งาน</h1>
            <p className="myticket-muted" style={{ marginBottom: 0 }}>
              หน้าดูตั๋วย้อนหลังยังไม่เปิดใช้งานในตอนนี้ ตั๋วของคุณยังอยู่ครบ —
              เปิดได้จากลิงก์ในอีเมลหรือ SMS ที่ได้รับตอนซื้อ หากเปิดไม่ได้ ติดต่อทีมงานพร้อมแจ้งรหัสคำสั่งซื้อ
            </p>
          </section>
        )}

        {screen === 'phone' && (
          <section className="myticket-card">
            {upgrading && (
              <button type="button" className="myticket-back" onClick={() => setScreen('list')}>
                ← ตั๋วบนเครื่องนี้
              </button>
            )}
            <h1>{upgrading ? 'ดูตั๋วทั้งหมดของคุณ' : 'ตั๋วของฉัน'}</h1>
            {/*
              Said out loud rather than shown as a bare form. Someone who opened this page yesterday
              and saw their tickets is owed an explanation for why it is asking today, and the honest
              one is also the reassuring one: the browser knows about more than one buyer, so it is
              asking which of them you are — not losing anybody's ticket.
            */}
            {deviceShared && !upgrading && (
              <div className="app-banner myticket-note">
                <strong>เครื่องนี้เคยจองให้หลายคน</strong>
                <span>
                  แต่ละคนใช้ชื่อและเบอร์ของตัวเอง เราจึงไม่แสดงรวมกัน — ยืนยันเบอร์ของคุณเพื่อดูเฉพาะตั๋วของคุณ
                </span>
              </div>
            )}
            <p className="myticket-muted">
              {upgrading
                ? 'ยืนยันเบอร์ที่ใช้ตอนซื้อ เพื่อดึงตั๋วที่ซื้อจากเครื่องอื่นมารวมไว้ที่นี่ด้วย'
                : 'กรอกเบอร์มือถือที่ใช้ตอนซื้อตั๋ว เราจะส่งรหัส 6 หลักไปให้ทาง SMS เพื่อยืนยันว่าเป็นคุณ'}
            </p>
            <input
              className="myticket-phone"
              inputMode="tel"
              maxLength={10}
              placeholder="08x-xxx-xxxx"
              value={phone}
              onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
              onKeyDown={(e) => { if (e.key === 'Enter') void requestOtp(); }}
              aria-label="เบอร์มือถือ"
            />
            {error && <p className="form-error">{error}</p>}
            <button className="btn-primary myticket-cta" disabled={busy} onClick={() => void requestOtp()}>
              ขอรหัสทาง SMS
            </button>
            <p className="myticket-fineprint">
              เราใช้เบอร์นี้เพื่อยืนยันตัวตนและค้นหาตั๋วเท่านั้น ไม่ส่งข้อความโฆษณา
            </p>
          </section>
        )}

        {screen === 'otp' && (
          <section className="myticket-card">
            <button type="button" className="myticket-back" onClick={() => { setScreen('phone'); setError(''); }}>
              ← แก้เบอร์
            </button>
            <h1>ใส่รหัสยืนยัน</h1>
            <p className="myticket-muted">
              ถ้าเบอร์ {maskPhone(phone)} มีตั๋วที่ซื้อไว้ เราได้ส่งรหัส 6 หลักไปให้แล้ว
            </p>
            <div className="myticket-otp">
              {otp.map((value, index) => (
                <input
                  key={index}
                  ref={(el) => { otpRefs.current[index] = el; }}
                  inputMode="numeric"
                  maxLength={1}
                  value={value}
                  aria-label={`รหัสหลักที่ ${index + 1}`}
                  onChange={(e) => onOtpChange(index, e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Backspace' && !otp[index] && index > 0) otpRefs.current[index - 1]?.focus();
                    if (e.key === 'Enter') void submitOtp();
                  }}
                />
              ))}
            </div>
            {error && <p className="form-error" aria-live="polite">{error}</p>}
            <div className="myticket-otp-meta">
              <span>{ttlLeft > 0 ? `หมดอายุใน ${Math.floor(ttlLeft / 60)}:${String(ttlLeft % 60).padStart(2, '0')}` : 'รหัสหมดอายุแล้ว'}</span>
              <button type="button" disabled={resendIn > 0 || busy} onClick={() => void requestOtp()}>
                {resendIn > 0 ? `ขอรหัสใหม่ได้ใน ${resendIn} วิ` : 'ขอรหัสใหม่'}
              </button>
            </div>
            <label className="myticket-remember">
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              <span>
                จำเครื่องนี้ไว้ 90 วัน
                <em>เครื่องนี้จะเห็นตั๋วของคุณโดยไม่ต้องใส่รหัสอีก — เอาออกได้จากปุ่ม “ออกจากเครื่องนี้”</em>
              </span>
            </label>
            <button className="btn-primary myticket-cta" disabled={busy} onClick={() => void submitOtp()}>ยืนยัน</button>
            {attemptsLeft !== null && attemptsLeft > 0 && (
              <p className="myticket-fineprint">ใส่รหัสผิดได้อีก {attemptsLeft} ครั้ง</p>
            )}
          </section>
        )}

        {screen === 'list' && (
          <section className="myticket-list">
            <div className={`myticket-banner${scope === 'phone' ? ' is-phone' : ''}`}>
              <strong>{scope === 'phone' ? 'ยืนยันเบอร์แล้ว' : 'จำเครื่องนี้ได้'}</strong>
              <span>
                {scope === 'phone'
                  ? 'เห็นตั๋วทุกใบที่ผูกกับเบอร์นี้'
                  : 'เห็นตั๋วที่ซื้อจากเครื่องนี้ทันที ไม่ต้องใส่รหัส'}
              </span>
            </div>
            <h1>ตั๋วของคุณ</h1>

            {!orders.length && (
              <p className="myticket-muted">
                ยังไม่พบตั๋วที่ผูกกับ{scope === 'phone' ? 'เบอร์นี้' : 'เครื่องนี้'} —
                ถ้าคุณซื้อด้วยเบอร์อื่น ลองยืนยันเบอร์นั้นดู
              </p>
            )}

            {!!upcoming.length && <h2 className="myticket-group">กำลังจะถึง</h2>}
            {upcoming.map((item) => <TicketRow key={item.code} item={item} onOpen={(c) => void openTicket(c)} />)}

            {!!past.length && <h2 className="myticket-group">ที่ผ่านมา</h2>}
            {past.map((item) => <TicketRow key={item.code} item={item} onOpen={(c) => void openTicket(c)} />)}

            {error && <p className="form-error">{error}</p>}

            {scope === 'device' ? (
              <>
                <button
                  className="btn-ghost myticket-cta"
                  onClick={() => { setUpgrading(true); setError(''); setScreen('phone'); }}
                >
                  ดูตั๋วทั้งหมดของเบอร์ฉัน →
                </button>
                <p className="myticket-fineprint">
                  ตั๋วที่ซื้อจากเครื่องอื่น หรือซื้อผ่านลิงก์ในแชท จะไม่อยู่ในรายการนี้จนกว่าจะยืนยันเบอร์
                </p>
              </>
            ) : (
              <>
                <button className="btn-link myticket-logout" onClick={() => void logout()}>ออกจากเครื่องนี้</button>
                <p className="myticket-fineprint">ล้างความจำของเครื่องนี้ และยกเลิกสิทธิ์ฝั่งเซิร์ฟเวอร์</p>
              </>
            )}
          </section>
        )}

        {screen === 'detail' && detail && (
          <section className="myticket-detail">
            <button type="button" className="myticket-back" onClick={() => { setDetail(null); setScreen('list'); }}>
              ← ตั๋วทั้งหมด
            </button>
            <article className="verified-ticket-card" aria-label={`บัตร ${detail.event.name} ${detail.order.code}`}>
              <div className="verified-ticket-status-row">
                <span className="verified-ticket-status">{detail.order.checkedInAt ? '✓ CHECKED IN' : '✓ PAYMENT CONFIRMED'}</span>
                <span className="verified-ticket-order">ORDER #{detail.order.code}</span>
              </div>
              <h1>{detail.event.name}</h1>
              <dl className="verified-ticket-facts verified-ticket-facts--summary">
                <div><dt>TICKETS / จำนวนบัตร</dt><dd>{detail.order.qty}</dd></div>
                <div><dt>ENTRY / เข้างานได้</dt><dd>{detail.order.entries} คน</dd></div>
              </dl>
              <div className="verified-ticket-tear" aria-hidden="true" />
              <div className="verified-ticket-qr-block">
                {/*
                  Same rule as the order page: the QR exists here only because the server sent one.
                  It withholds it for an event more than 30 days past, and the client shows what it
                  was given rather than deciding for itself.
                */}
                {!detail.qrVisible ? (
                  <div className="verified-ticket-qr-loading">
                    QR ถูกปิดหลังงานจบแล้ว<br />เก็บหน้านี้ไว้เป็นหลักฐานการซื้อได้
                  </div>
                ) : qrDataUrl ? (
                  <div className="verified-ticket-qr">
                    <img src={qrDataUrl} alt={`QR สำหรับเช็กอิน ${detail.order.code}`} width={180} height={180} />
                  </div>
                ) : <div className="verified-ticket-qr-loading" aria-live="polite">กำลังสร้าง QR…</div>}
                <div className="verified-ticket-code">{detail.order.code}</div>
                {detail.qrVisible && detail.order.qrPayload && (
                  <div className="verified-ticket-help">
                    แสดง QR นี้ที่ประตูทางเข้า · ใช้ได้ครั้งเดียว<br />ไม่ต้องพิมพ์ — เปิดจากมือถือได้เลย
                  </div>
                )}
              </div>
            </article>

            <section className="verified-ticket-panel">
              <h2>รายการบัตร / TICKETS</h2>
              <div className="verified-ticket-lines">
                {detail.order.lines.map((line) => (
                  <div className="verified-ticket-line" key={line.ticketId}>
                    <div><strong>{line.label}</strong></div>
                    <div>{fmt(line.lineTotal)}</div>
                  </div>
                ))}
                <div className="verified-ticket-total">
                  <span>TOTAL PAID · โอนผ่านธนาคาร</span><strong>{fmt(detail.order.total)}</strong>
                </div>
              </div>
            </section>
          </section>
        )}
      </main>
    </div>
  );
}
